import { useMemo } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { shortResourceId } from "../../lib/shellNavigation";
import type { ListQueryResult } from "../../lib/useListQuery";
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  Inline,
  InlineError,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import {
  ScenarioEditor,
  type ScenarioAgentOption,
  type ScenarioFormSubmission,
} from "./ScenarioEditor";
import {
  ScenarioActions,
  type ScenarioActionHandlers,
} from "./ScenarioActions";

type ScenarioResponse = components["schemas"]["ScenarioResponse"];

export type ScenarioDrawerState =
  | { mode: "create" }
  | { mode: "edit"; scenario: ScenarioResponse }
  | { mode: "loading" }
  | { message: string; mode: "error" };

export interface ScenariosListViewProps {
  // Agent display names for the unscoped Agent column.
  agentNamesById?: ReadonlyMap<string, string>;
  // Owning-agent choices for the unscoped create drawer; absent under an
  // agent scope (the editor then uses `scopedAgentId`).
  agentOptions?: ScenarioAgentOption[];
  deleteError: string | null;
  deleteScenarioId: string | null;
  drawer: ScenarioDrawerState | null;
  isDeletePending: boolean;
  isSavePending: boolean;
  onCancelDelete: () => void;
  onCloseEditor: () => void;
  onConfirmDelete: (scenario: ScenarioResponse) => void;
  onOpenEdit: (scenarioId: string) => void;
  onSave: (submission: ScenarioFormSubmission) => Promise<void>;
  onStartDelete: (scenario: ScenarioResponse) => void;
  saveError: string | null;
  scenarios: ListQueryResult<ScenarioResponse>;
  scopedAgentId?: string;
}

function scenarioColumns(
  handlers: ScenarioActionHandlers & {
    agentNamesById?: ReadonlyMap<string, string>;
    showAgentColumn: boolean;
  },
): Array<ColumnDef<ScenarioResponse>> {
  const agentName = (scenario: ScenarioResponse) =>
    handlers.agentNamesById?.get(scenario.agent_id) ??
    shortResourceId(scenario.agent_id);

  const columns: Array<ColumnDef<ScenarioResponse>> = [
    {
      header: "Name",
      id: "name",
      accessorFn: (scenario) => scenario.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Scenario ID"
          name={row.original.display_name}
          strong
        />
      ),
    },
  ];

  if (handlers.showAgentColumn) {
    columns.push({
      header: "Agent",
      id: "agent",
      accessorFn: (scenario) => agentName(scenario),
      cell: ({ row }) => (
        <NameWithId
          id={row.original.agent_id}
          idLabel="Agent ID"
          name={agentName(row.original)}
        />
      ),
    });
  }

  columns.push(
    {
      header: "Mode",
      id: "mode",
      accessorFn: (scenario) => scenario.current_revision.mode,
      cell: ({ row }) => <Badge>{row.original.current_revision.mode}</Badge>,
    },
    {
      header: "Revision",
      id: "revision",
      accessorFn: (scenario) => scenario.current_revision.revision,
    },
    {
      header: "Updated",
      id: "updated",
      accessorFn: (scenario) => scenario.updated_at,
      cell: ({ row }) => <DateCell value={row.original.updated_at} />,
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => (
        <ScenarioActions handlers={handlers} scenario={row.original} />
      ),
    },
  );

  return columns;
}

export function ScenariosListView({
  agentNamesById,
  agentOptions,
  deleteError,
  deleteScenarioId,
  drawer,
  isDeletePending,
  isSavePending,
  onCancelDelete,
  onCloseEditor,
  onConfirmDelete,
  onOpenEdit,
  onSave,
  onStartDelete,
  saveError,
  scenarios,
  scopedAgentId,
}: ScenariosListViewProps) {
  const showAgentColumn = scopedAgentId === undefined;
  const columns = useMemo(
    () =>
      scenarioColumns({
        agentNamesById,
        deleteError,
        deleteScenarioId,
        isDeletePending,
        onCancelDelete,
        onConfirmDelete,
        onOpenEdit,
        onStartDelete,
        showAgentColumn,
      }),
    [
      agentNamesById,
      deleteError,
      deleteScenarioId,
      isDeletePending,
      onCancelDelete,
      onConfirmDelete,
      onOpenEdit,
      onStartDelete,
      showAgentColumn,
    ],
  );
  return (
    <>
      <PaginatedTableSection
        ariaLabel="Scenarios"
        columns={columns}
        emptyState={<EmptyState title="No scenarios" />}
        enableSorting
        errorMessage={scenarios.errorMessage}
        getRowId={(scenario) => scenario.id}
        hasNextPage={scenarios.hasNextPage}
        isFetchingNextPage={scenarios.isFetchingNextPage}
        isPending={scenarios.isPending}
        items={scenarios.items}
        listStateKey={scenarios.listStateKey}
        onLoadMore={scenarios.fetchNextPage}
        onRowClick={(row) => {
          onOpenEdit(row.original.id);
        }}
        skeletonLabel="Loading scenarios"
        virtual={{ fill: true, interactiveRows: true }}
      />
      {drawer ? (
        <Drawer
          ariaLabel={
            drawer.mode === "create" ? "Create scenario" : "Edit scenario"
          }
          isOpen
          onDismiss={onCloseEditor}
        >
          {drawer.mode === "loading" ? (
            <Skeleton label="Loading scenario" rows={5} shape="detail-field" />
          ) : drawer.mode === "error" ? (
            <Stack gap={4}>
              <Text variant="caps">Edit scenario</Text>
              <InlineError>{drawer.message}</InlineError>
              <Inline>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onCloseEditor}
                >
                  Close
                </Button>
              </Inline>
            </Stack>
          ) : (
            <ScenarioEditor
              agentOptions={drawer.mode === "create" ? agentOptions : undefined}
              error={saveError}
              isPending={isSavePending}
              scenario={drawer.mode === "edit" ? drawer.scenario : null}
              scopedAgentId={scopedAgentId}
              onCancel={onCloseEditor}
              onSubmit={onSave}
            />
          )}
        </Drawer>
      ) : null}
    </>
  );
}
